// Coaching (#/accompagnement) : calendrier mensuel par défaut, vue Liste, et deux pages contextuelles par le hash —
// #/accompagnement?view=consult (Consultations) et #/accompagnement?view=genius[&session=sX] (Team Genius, séance passée).
// Composants réels de ubm-ui-components ; données de ./exchanges-fixtures (lecture seule). Les soldes, rendez-vous, pistes et
// questions passent par un CoachingStore : la racine le raccorde au store partagé avec connectCoachingStore() ; sans
// raccord, un repli local le remplace et l'écran le dit (« Simulation locale »). Aucun réseau, aucun paiement, aucun envoi.
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore, type FocusEvent, type PointerEvent } from "react";
import { ArrowLeft, ChevronLeft, ChevronRight, FileText, MapPin, PlayCircle, SlidersHorizontal } from "lucide-react";
import type { PageProps } from "./contracts";
import { Composer, MOBILE_QUERY, useMediaQuery } from "./shared";
import { eventVisual } from "./event-visuals";
import { Alert } from "../vendor/ubm-ui-components/src/components/alert";
import { Avatar } from "../vendor/ubm-ui-components/src/components/avatar";
import { Badge, type BadgeTone } from "../vendor/ubm-ui-components/src/components/badge";
import { Button } from "../vendor/ubm-ui-components/src/components/button";
import { CardSurface } from "../vendor/ubm-ui-components/src/components/card";
import { ChipGroup } from "../vendor/ubm-ui-components/src/components/chip-group";
import { EmptyState } from "../vendor/ubm-ui-components/src/components/empty-state";
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from "../vendor/ubm-ui-components/src/components/popover";
import { SegmentedControl } from "../vendor/ubm-ui-components/src/components/segmented-control";
import { Textarea } from "../vendor/ubm-ui-components/src/components/textarea";
import { AvailabilityPicker, type AvailabilityDay } from "../vendor/ubm-ui-components/src/blocks/availability-picker";
import {
  AGENDA_ACCESS_LABEL, AGENDA_EVENTS, AGENDA_SOURCES, BOOKING_HORIZON_DAYS, CONSULT_POLICY, DAYS_SHORT_FR, DEMO_TODAY,
  GENIUS_CATCHUP_POLICY, GENIUS_NEXT, GENIUS_PAST, GENIUS_QUESTIONS, INITIAL_APPOINTMENTS, INITIAL_BALANCES, MEMBER, MONTHS_FR, SERVICES, SLOT_RULES,
  TIME_ZONE_LABEL, addDaysIso, addMinutes, asset, at, capitalize, diffDays, hourLabel, longDate, parseIso, serviceById, toIso,
  type AgendaItem, type AgendaSourceId, type Appointment, type CoinKind, type GeniusQuestion, type GeniusSession, type ServiceId,
} from "./exchanges-fixtures";
import "./coaching.css";

// ── Raccord racine : CoachingStore ───────────────────────────────────────────

export interface CoachingSnapshot {
  balances: Record<CoinKind, number>;
  appointments: Appointment[];
  /** Pistes Team Genius ajoutées aux actions du membre (Agir). */
  leads: Record<string, boolean>;
  /** Motifs de rattrapage envoyés, par séance Team Genius. */
  catchups: Record<string, string>;
  questions: GeniusQuestion[];
}
export interface CoachingActions {
  /** Réserve avec une pièce de la catégorie du service. Le paiement par carte n'est jamais simulé ici. */
  confirmBooking(input: { service: ServiceId; date: string; time: string; prep: string }): void;
  rescheduleAppointment(id: string, date: string, time: string): void;
  cancelAppointment(id: string): void;
  toggleLead(id: string): void;
  requestCatchup(sessionId: string, reason: string): void;
  askQuestion(text: string): void;
}
export interface CoachingStore {
  subscribe(listener: () => void): () => void;
  getSnapshot(): CoachingSnapshot;
  actions: CoachingActions;
  /** true tant que les actions ne touchent que ce navigateur (affiché au membre). */
  simulated: boolean;
}

/** Repli local, amorcé par les fixtures existantes ; il ne remplace aucun backend et l'écran l'annonce. */
function createLocalStore(): CoachingStore {
  let seq = 1;
  let snap: CoachingSnapshot = {
    balances: { ...INITIAL_BALANCES },
    appointments: INITIAL_APPOINTMENTS.map(a => ({ ...a, history: [...a.history] })),
    leads: {}, catchups: {}, questions: [...GENIUS_QUESTIONS],
  };
  const listeners = new Set<() => void>();
  const set = (fn: (s: CoachingSnapshot) => CoachingSnapshot) => { snap = fn(snap); listeners.forEach(l => l()); };
  return {
    simulated: true,
    subscribe: l => { listeners.add(l); return () => { listeners.delete(l); }; },
    getSnapshot: () => snap,
    actions: {
      confirmBooking: ({ service, date, time, prep }) => set(s => {
        const coin = serviceById(service).coin;
        if (s.balances[coin] < 1) return s;
        const appointment: Appointment = { id: `lb${seq++}`, service, date, time, payment: { kind: "credit" }, status: "confirmed", link: false, prep, history: [], reschedules: 0 };
        return { ...s, balances: { ...s.balances, [coin]: s.balances[coin] - 1 }, appointments: [...s.appointments, appointment] };
      }),
      rescheduleAppointment: (id, date, time) => set(s => ({ ...s, appointments: s.appointments.map(a => a.id !== id ? a : { ...a, date, time, link: false,
        reschedules: a.reschedules + 1, history: [...a.history, `Reporté depuis le ${longDate(a.date)} à ${hourLabel(a.time)}, sans nouveau débit`] }) })),
      cancelAppointment: id => set(s => {
        const a = s.appointments.find(x => x.id === id);
        if (!a || a.status !== "confirmed") return s;
        const sv = serviceById(a.service);
        return { ...s,
          balances: a.payment.kind === "credit" ? { ...s.balances, [sv.coin]: s.balances[sv.coin] + 1 } : s.balances,
          appointments: s.appointments.map(x => x.id !== id ? x : { ...x, status: "cancelled", history: [...x.history,
            a.payment.kind === "credit" ? `1 ${sv.coinName} restituée à l’annulation` : `Remboursement bancaire de ${a.payment.amount} € à traiter séparément`] }) };
      }),
      toggleLead: id => set(s => ({ ...s, leads: { ...s.leads, [id]: !s.leads[id] } })),
      requestCatchup: (sessionId, reason) => set(s => ({ ...s, catchups: { ...s.catchups, [sessionId]: reason } })),
      askQuestion: text => set(s => ({ ...s, questions: [...s.questions, { id: `lq${seq++}`, author: MEMBER.name, time: "à l’instant", text }] })),
    },
  };
}

let activeStore: CoachingStore = createLocalStore();
const storeListeners = new Set<() => void>();
/** Point de raccord : la racine branche ici le store partagé (soldes et rendez-vous de Boutique, Miles, Agir). */
export function connectCoachingStore(store: CoachingStore) { activeStore = store; storeListeners.forEach(l => l()); }

function useCoaching() {
  const store = useSyncExternalStore(l => { storeListeners.add(l); return () => { storeListeners.delete(l); }; }, () => activeStore, () => activeStore);
  const snap = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
  return { snap, actions: store.actions, simulated: store.simulated };
}

// ── Adresse (hash) ───────────────────────────────────────────────────────────

type CoachingView = "consult" | "genius";
interface CoachingLocation { view?: CoachingView; session?: string }

function readCoachingHash(): CoachingLocation {
  const raw = window.location.hash.replace(/^#\/?/, "");
  const q = raw.indexOf("?");
  if (q < 0 || raw.slice(0, q) !== "accompagnement") return {};
  const params = new URLSearchParams(raw.slice(q + 1));
  const view = params.get("view");
  if (view === "consult") return { view };
  if (view !== "genius") return {};
  const session = params.get("session");
  return session && GENIUS_PAST.some(s => s.id === session) ? { view, session } : { view };
}
const coachingHash = (loc: CoachingLocation) => {
  const params = new URLSearchParams();
  if (loc.view) params.set("view", loc.view);
  if (loc.view === "genius" && loc.session) params.set("session", loc.session);
  const qs = params.toString();
  return `#/accompagnement${qs ? `?${qs}` : ""}`;
};
const sameLocation = (a: CoachingLocation, b: CoachingLocation) => a.view === b.view && a.session === b.session;

/** Miroir local du hash : pushState pour les gestes internes, hashchange/popstate pour retour arrière et liens saisis. */
function useCoachingLocation() {
  const [loc, setLoc] = useState<CoachingLocation>(readCoachingHash);
  useEffect(() => {
    const sync = () => { const next = readCoachingHash(); setLoc(prev => (sameLocation(prev, next) ? prev : next)); };
    window.addEventListener("hashchange", sync);
    window.addEventListener("popstate", sync);
    return () => { window.removeEventListener("hashchange", sync); window.removeEventListener("popstate", sync); };
  }, []);
  const go = useCallback((next: CoachingLocation) => {
    const hash = coachingHash(next);
    if (window.location.hash !== hash) window.history.pushState(null, "", hash);
    setLoc(prev => (sameLocation(prev, next) ? prev : next));
  }, []);
  return [loc, go] as const;
}

// ── Modèle du calendrier ─────────────────────────────────────────────────────

interface CalItem {
  id: string;
  type: AgendaSourceId;
  title: string;
  date: string;
  start: string;
  end: string;
  place: string;
  access: string;
  tone: BadgeTone;
  appointment?: string;
  event?: AgendaItem;
}

const TYPE_NAME = Object.fromEntries(AGENDA_SOURCES.map(s => [s.id, s.name])) as Record<AgendaSourceId, string>;
const byWhen = (x: { date: string; start: string }, y: { date: string; start: string }) => (x.date + x.start).localeCompare(y.date + y.start);

function calendarItems(appointments: Appointment[]): CalItem[] {
  const own: CalItem[] = appointments.filter(a => a.status !== "cancelled").map(a => {
    const sv = serviceById(a.service);
    const past = a.status === "done" || diffDays(a.date) < 0;
    return { id: a.id, type: "consult", title: sv.collective ? sv.name : `Consultation ${sv.id === "arnaud" ? "avec Arnaud" : sv.name}`, date: a.date, start: a.time,
      end: addMinutes(a.time, sv.duration), place: "Visio", access: past ? "Passé" : "Réservé", tone: past ? "neutral" : "success", appointment: a.id };
  });
  // Un événement « À réserver » déjà réservé par le membre n'apparaît qu'une fois : sous son rendez-vous.
  const events: CalItem[] = AGENDA_EVENTS
    .filter(e => !(e.next?.service && own.some(o => o.date === e.date && o.start === e.start)))
    .map(e => ({ id: e.id, type: e.source, title: e.title, date: e.date, start: e.start, end: e.end, place: e.place, access: diffDays(e.date) < 0 ? "Passé" : AGENDA_ACCESS_LABEL[e.access],
      tone: e.access === "booked" ? "success" : e.access === "included" ? "accent" : "neutral", event: e }));
  return [...own, ...events].sort(byWhen);
}

const whenLabel = (i: { date: string; start: string; end: string }) => `${capitalize(longDate(i.date))} · ${hourLabel(i.start)}–${hourLabel(i.end)}`;
const monthLabel = (month: Date) => `${capitalize(MONTHS_FR[month.getMonth()]!)} ${month.getFullYear()}`;
const WEEKDAYS = ["lun.", "mar.", "mer.", "jeu.", "ven.", "sam.", "dim."];

/** Semaines (lundi en tête) qui couvrent le mois affiché, ni plus ni moins. */
function monthWeeks(month: Date): string[][] {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const last = new Date(month.getFullYear(), month.getMonth() + 1, 0);
  const start = new Date(first); start.setDate(1 - ((first.getDay() + 6) % 7));
  const end = new Date(last); end.setDate(last.getDate() + (6 - ((last.getDay() + 6) % 7)));
  const weeks: string[][] = [];
  for (const d = new Date(start); d <= end; d.setDate(d.getDate() + 7)) {
    weeks.push(Array.from({ length: 7 }, (_, i) => { const x = new Date(d); x.setDate(d.getDate() + i); return toIso(x); }));
  }
  return weeks;
}

// ── Petits assemblages ───────────────────────────────────────────────────────

function Coin({ kind, size = "md" }: { kind: CoinKind; size?: "md" | "lg" }) {
  const sv = SERVICES.find(s => s.coin === kind);
  return <span className={`co-coin co-coin-${kind} co-coin-${size}`} role="img" aria-label={sv ? capitalize(sv.coinName) : kind} />;
}

function Facts({ rows }: { rows: [string, string][] }) {
  return <dl className="co-facts">{rows.map(([k, v]) => <div key={k}><dt>{k}</dt><dd>{v}</dd></div>)}</dl>;
}

function TypeDot({ type }: { type: AgendaSourceId }) {
  return <span className={`co-dot co-type-${type}`} aria-hidden="true" />;
}

// ── Détails (sidepeek partagé) ───────────────────────────────────────────────

const statusLabel = (a: Appointment) => a.status === "cancelled" ? "Annulé" : a.status === "done" || diffDays(a.date) < 0 ? "Passé" : "Confirmé";
const isUpcoming = (a: Appointment) => a.status === "confirmed" && diffDays(a.date) >= 0;
const paymentLabel = (a: Appointment) => a.payment.kind === "credit" ? `1 ${serviceById(a.service).coinName}` : `Carte · ${a.payment.amount} €`;

const PICKER_STRINGS = {
  days: "Jours", times: (day: string) => `Créneaux du ${day}`, busy: "complet", none: "Plus de créneau ce jour-là.", choose: "Choisis un créneau",
  selected: (day: string, time: string) => `${day} à ${time}`, confirm: "Continuer", confirming: "Vérification…", confirmed: (day: string, time: string) => `Créneau retenu : ${day} à ${time}.`,
  failed: "Ce créneau n’a pas pu être retenu. Ton choix est conservé.", retry: "Réessayer", unavailable: "Réservation indisponible ici.",
  taken: "Ce créneau n’est plus disponible. Choisis-en un autre.", attendees: "Intervenant",
};

function availabilityDays(service: ServiceId, appointments: Appointment[]): AvailabilityDay[] {
  const rule = SLOT_RULES[service], duration = serviceById(service).duration;
  return Array.from({ length: BOOKING_HORIZON_DAYS }, (_, i) => addDaysIso(DEMO_TODAY, i)).flatMap(iso => {
    const n = diffDays(iso);
    if (!rule.weekdays.includes(parseIso(iso).getDay())) return [];
    const times = (n === 0 ? rule.todayTimes ?? [] : rule.times)
      .filter(t => !appointments.some(a => a.status === "confirmed" && a.service === service && a.date === iso && a.time === t));
    if (!times.length) return [];
    const places = rule.places?.(n);
    return [{ id: iso, weekday: n === 0 ? "auj." : DAYS_SHORT_FR[parseIso(iso).getDay()]!, day: String(parseIso(iso).getDate()), label: capitalize(longDate(iso)),
      slots: times.map(t => ({ id: t, start: at(iso, t), end: at(iso, addMinutes(t, duration)), label: hourLabel(t) + (places ? ` · ${places} places` : "") })) }];
  });
}

function BookingPanel({ serviceId, rescheduleOf, onShop }: { serviceId: ServiceId; rescheduleOf?: string; onShop: () => void }) {
  const { snap, actions } = useCoaching();
  const sv = serviceById(serviceId);
  const original = rescheduleOf ? snap.appointments.find(a => a.id === rescheduleOf) : undefined;
  const [slot, setSlot] = useState<{ date: string; time: string } | null>(null);
  const [prep, setPrep] = useState("");
  const [done, setDone] = useState<{ before: number } | null>(null);
  const balance = snap.balances[sv.coin];
  const days = useMemo(() => availabilityDays(serviceId, snap.appointments), [serviceId, snap.appointments]);
  const attendees = sv.who === "Arnaud" ? [{ name: "Arnaud", avatar: asset("tc-arnaud-a-propos.jpg") }] : [];

  if (done && slot) return <div className="co-stack">
    <Alert tone="success" title={original ? "Ton rendez-vous est déplacé" : "Ton rendez-vous est réservé"}>{sv.name} · {capitalize(longDate(slot.date))} · {hourLabel(slot.time)}</Alert>
    <p className="co-muted">{original ? "Règlement initial conservé, aucun nouveau débit." : `1 ${sv.coinName} utilisée · solde ${done.before} → ${done.before - 1}.`}</p>
  </div>;

  if (!original && balance < 1) return <div className="co-stack">
    <Alert tone="info" title={`Aucune ${sv.coinName} disponible`}>Chaque pièce vaut pour sa catégorie. Le règlement de {sv.price} € par carte se fait depuis la Boutique.</Alert>
    <div className="co-actions"><Button variant="secondary" onClick={onShop}>Voir la Boutique</Button></div>
  </div>;

  if (!slot) return <div className="co-stack">
    <p className="co-muted">{sv.mode} · {sv.duration} min</p>
    {days.length ? <AvailabilityPicker title={sv.name} duration={`${sv.duration} min`} timeZoneLabel={TIME_ZONE_LABEL} attendees={attendees} days={days}
      strings={PICKER_STRINGS} onConfirm={sel => setSlot({ date: sel.dayId, time: sel.slotId })} />
      : <Alert tone="info" title="Aucun créneau sur les deux prochaines semaines">L’équipe ouvre de nouveaux créneaux régulièrement. Tu peux lui écrire depuis Messages.</Alert>}
  </div>;

  const confirm = () => {
    setDone({ before: balance });
    if (original) actions.rescheduleAppointment(original.id, slot.date, slot.time);
    else actions.confirmBooking({ service: serviceId, date: slot.date, time: slot.time, prep: prep.trim() });
  };
  return <div className="co-stack">
    <Facts rows={[["Prestation", sv.name], ["Date", capitalize(longDate(slot.date))], ["Heure", `${hourLabel(slot.time)}–${hourLabel(addMinutes(slot.time, sv.duration))} · ${TIME_ZONE_LABEL}`],
      ["Intervenant", sv.who], ["Règlement", original ? `Conservé : ${paymentLabel(original)} · aucun nouveau débit` : `1 ${sv.coinName} · solde ${balance} → ${balance - 1}`]]} />
    <p className="co-small">{CONSULT_POLICY}</p>
    {!original ? <Textarea label="Ta question pour la séance (facultatif)" rows={2} value={prep} onChange={e => setPrep(e.target.value)} placeholder="Ce que tu veux travailler…" /> : null}
    <div className="co-actions">
      <Button variant="ghost" onClick={() => setSlot(null)}>Changer de créneau</Button>
      <Button onClick={confirm}>{original ? "Confirmer le report" : `Confirmer et utiliser 1 ${sv.coinName}`}</Button>
    </div>
  </div>;
}

function AppointmentPanel({ id, onShop }: { id: string; onShop: () => void }) {
  const { snap, actions } = useCoaching();
  const a = snap.appointments.find(x => x.id === id);
  const [mode, setMode] = useState<"view" | "reschedule" | "cancel">("view");
  if (!a) return <p className="co-muted">Ce rendez-vous n’existe plus.</p>;
  if (mode === "reschedule") return <BookingPanel serviceId={a.service} rescheduleOf={a.id} onShop={onShop} />;
  const sv = serviceById(a.service), up = isUpcoming(a);
  return <div className="co-stack">
    <img className="co-visual" src={eventVisual("consult")} alt="" />
    <div className="co-row"><Coin kind={sv.coin} size="lg" /><Badge tone={up ? "success" : "neutral"}>{statusLabel(a)}</Badge></div>
    <p className="co-lead">{whenLabel({ date: a.date, start: a.time, end: addMinutes(a.time, sv.duration) })}</p>
    <Facts rows={[["Lieu", "Visio"], ["Intervenant", sv.who], ["Modalité", sv.mode], ["Règlement", paymentLabel(a)]]} />
    {up ? <p className="co-small">Le lien de visio apparaîtra ici dès qu’il sera communiqué par l’équipe.</p> : null}
    {a.prep ? <div className="co-stack-sm"><p className="co-kicker">Ta question pour la séance</p><p>{a.prep}</p></div> : null}
    {a.history.length ? <ul className="co-history">{a.history.map(h => <li key={h}>{h}</li>)}</ul> : null}
    {a.status === "done" ? <div className="co-stack-sm">
      <p className="co-muted">{a.replay ? "Replay indisponible" : "Pas de replay pour cette consultation."}</p>
      {a.action ? <CardSurface variant="sunken" padding="sm"><p className="co-small">Action issue de cette consultation</p><p className="co-strong">{a.action}</p></CardSurface> : null}
    </div> : null}
    {up && mode === "view" ? <div className="co-stack-sm">
      <p className="co-small">{CONSULT_POLICY}{a.reschedules >= 1 ? " Le report de ce rendez-vous a déjà été utilisé." : ""}</p>
      <div className="co-actions">
        <Button variant="secondary" disabled={a.reschedules >= 1} onClick={() => setMode("reschedule")}>Reporter</Button>
        <Button variant="ghost" onClick={() => setMode("cancel")}>Annuler le rendez-vous</Button>
      </div>
    </div> : null}
    {mode === "cancel" ? <Alert tone="warning" title="Annuler ce rendez-vous ?">
      <p>{a.payment.kind === "credit" ? `Ta ${sv.coinName} est restituée : solde ${snap.balances[sv.coin]} → ${snap.balances[sv.coin] + 1}.` : `Ton paiement de ${a.payment.amount} € relève d’un remboursement bancaire distinct.`}</p>
      <div className="co-actions"><Button variant="ghost" onClick={() => setMode("view")}>Garder le rendez-vous</Button><Button variant="danger" onClick={() => { actions.cancelAppointment(a.id); setMode("view"); }}>Annuler le rendez-vous</Button></div>
    </Alert> : null}
  </div>;
}

interface EventDetailActions { onBook: (service: ServiceId) => void; onRoute: PageProps["navigate"]; onView: (loc: CoachingLocation) => void }

function EventPanel({ item, actions }: { item: CalItem; actions: EventDetailActions }) {
  const e = item.event;
  const session = item.type === "tg" ? GENIUS_PAST.find(s => s.date === item.date) : undefined;
  return <div className="co-stack">
    <img className="co-visual" src={eventVisual(item.type)} alt="" />
    <div className="co-row"><Badge tone="neutral" icon={<TypeDot type={item.type} />}>{TYPE_NAME[item.type]}</Badge><Badge tone={item.tone}>{item.access}</Badge></div>
    <p className="co-lead">{whenLabel(item)}</p>
    {e ? <p>{e.desc}</p> : null}
    <Facts rows={[["Lieu", item.place], ...(e?.where ? [["Accès", e.where] as [string, string]] : []), ...(e?.org ? [["Organisé par", e.org] as [string, string]] : [])]} />
    {e?.access === "register" ? <p className="co-small">L’inscription aux étapes de la Tournée se fait auprès de l’organisateur.</p> : null}
    <div className="co-actions">
      {item.type === "tg" ? <Button variant="secondary" onClick={() => actions.onView(session ? { view: "genius", session: session.id } : { view: "genius" })}>{session ? "Ouvrir la séance" : "Voir Team Genius"}</Button> : null}
      {e?.next?.service && diffDays(e.date) >= 0 ? <Button onClick={() => actions.onBook(e.next!.service!)}>{e.next.label}</Button>
        : e?.next && e.next.route !== "accompagnement" ? <Button onClick={() => actions.onRoute(e.next!.route)}>{e.next.label}</Button> : null}
    </div>
  </div>;
}

// ── Calendrier mensuel ───────────────────────────────────────────────────────

const HOVER_OPEN_MS = 180, HOVER_CLOSE_MS = 140;

/** Une puce d'événement : survol souris ou focus clavier = aperçu Popover ; clic ou toucher = détail dans le sidepeek. */
function EventChip({ item, onOpen }: { item: CalItem; onOpen: (item: CalItem) => void }) {
  const [open, setOpen] = useState(false);
  const timer = useRef(0);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const later = (next: boolean, ms: number) => { window.clearTimeout(timer.current); timer.current = window.setTimeout(() => setOpen(next), ms); };
  const mouse = (e: PointerEvent) => e.pointerType === "mouse";
  return <Popover open={open} onOpenChange={setOpen}>
    <PopoverAnchor asChild>
      <span className="co-chip-anchor">
        <Button variant="ghost" size="xs" className={`co-chip co-type-${item.type}`} data-past={diffDays(item.date) < 0 || undefined}
          aria-label={`${item.title}, ${whenLabel(item)}, ${item.place}, ${item.access}`}
          onPointerEnter={e => { if (mouse(e)) later(true, HOVER_OPEN_MS); }} onPointerLeave={e => { if (mouse(e)) later(false, HOVER_CLOSE_MS); }}
          onFocus={(e: FocusEvent<HTMLButtonElement>) => { if (e.currentTarget.matches(":focus-visible")) later(true, 0); }} onBlur={() => later(false, 0)}
          onClick={() => { window.clearTimeout(timer.current); setOpen(false); onOpen(item); }}>
          <TypeDot type={item.type} /><span className="co-chip-time">{hourLabel(item.start).replace(" h ", ":")}</span><span className="co-chip-title">{item.title}</span>
        </Button>
      </span>
    </PopoverAnchor>
    <PopoverContent side="bottom" align="start" className="co-preview" onOpenAutoFocus={e => e.preventDefault()} onCloseAutoFocus={e => e.preventDefault()}
      onPointerEnter={() => window.clearTimeout(timer.current)} onPointerLeave={() => later(false, HOVER_CLOSE_MS)}>
      <div className={`co-preview-bar co-type-${item.type}`} aria-hidden="true" />
      <p className="co-kicker">{TYPE_NAME[item.type]}</p>
      <p className="co-preview-title">{item.title}</p>
      <p className="co-small">{whenLabel(item)}</p>
      <p className="co-small co-row-inline"><MapPin size={14} aria-hidden="true" />{item.place}</p>
      <div className="co-row"><Badge size="sm" tone={item.tone}>{item.access}</Badge></div>
      <p className="co-small co-muted">Clique pour ouvrir le détail.</p>
    </PopoverContent>
  </Popover>;
}

const MAX_CHIPS = 3;

function MonthGrid({ month, items, mobile, selected, onSelect, onOpen, onMore }: {
  month: Date; items: CalItem[]; mobile: boolean; selected: string; onSelect: (iso: string) => void; onOpen: (item: CalItem) => void; onMore: (iso: string) => void;
}) {
  const weeks = useMemo(() => monthWeeks(month), [month]);
  const byDay = useMemo(() => { const m = new Map<string, CalItem[]>(); for (const i of items) m.set(i.date, [...(m.get(i.date) ?? []), i]); return m; }, [items]);
  return <table className="co-month" data-mobile={mobile || undefined} aria-label={`Calendrier, ${monthLabel(month)}`}>
    <thead><tr>{WEEKDAYS.map(d => <th key={d} scope="col">{d}</th>)}</tr></thead>
    <tbody>{weeks.map(week => <tr key={week[0]}>{week.map(iso => {
      const day = parseIso(iso), list = byDay.get(iso) ?? [];
      const outside = day.getMonth() !== month.getMonth();
      const today = iso === DEMO_TODAY;
      if (mobile) return <td key={iso} data-outside={outside || undefined}>
        <button type="button" className="co-daybtn" aria-pressed={selected === iso} data-today={today || undefined}
          aria-label={`${capitalize(longDate(iso))}, ${list.length === 0 ? "aucun événement" : list.length === 1 ? "1 événement" : `${list.length} événements`}`} onClick={() => onSelect(iso)}>
          <span className="co-daynum">{day.getDate()}</span>
          <span className="co-dots">{list.slice(0, 3).map(i => <TypeDot key={i.id} type={i.type} />)}</span>
        </button>
      </td>;
      return <td key={iso} data-outside={outside || undefined} data-today={today || undefined}>
        <span className="co-daynum">{day.getDate()}</span>
        <div className="co-chips">
          {list.slice(0, MAX_CHIPS).map(i => <EventChip key={i.id} item={i} onOpen={onOpen} />)}
          {list.length > MAX_CHIPS ? <Button variant="ghost" size="xs" className="co-more" onClick={() => onMore(iso)}>+{list.length - MAX_CHIPS} de plus</Button> : null}
        </div>
      </td>;
    })}</tr>)}</tbody>
  </table>;
}

function ItemRow({ item, onOpen }: { item: CalItem; onOpen: (item: CalItem) => void }) {
  return <button type="button" className={`co-item co-event-row co-type-${item.type}`} onClick={() => onOpen(item)}>
    <img className="co-event-poster" src={eventVisual(item.type)} alt="" loading="lazy" />
    <span className="co-item-text"><span className="co-event-meta"><TypeDot type={item.type} />{hourLabel(item.start)} · {TYPE_NAME[item.type]}</span><b>{item.title}</b><span>{item.place}</span></span>
    <span className="co-event-access">{item.access}</span><ChevronRight size={16} aria-hidden="true" />
  </button>;
}

function ListView({ items, onOpen }: { items: CalItem[]; onOpen: (item: CalItem) => void }) {
  const groups = useMemo(() => { const m = new Map<string, CalItem[]>(); for (const i of items) m.set(i.date, [...(m.get(i.date) ?? []), i]); return [...m.entries()]; }, [items]);
  if (!groups.length) return <EmptyState title="Aucun événement ce mois-ci" description="Parcours les autres mois pour retrouver tes séances." titleAs="h3" />;
  return <div className="co-list co-timeline">{groups.map(([iso, list]) => {
    const date = parseIso(iso);
    return <section key={iso} className="co-list-day" aria-label={capitalize(longDate(iso))}>
      <div className="co-date-marker" data-today={iso === DEMO_TODAY || undefined}><span>{DAYS_SHORT_FR[date.getDay()]}</span><b>{date.getDate()}</b></div>
      <ul className="co-plain">{list.map(i => <li key={i.id}><ItemRow item={i} onOpen={onOpen} /></li>)}</ul>
    </section>;
  })}</div>;
}

function AccessCard({ title, line, sub, image, onOpen }: { title: string; line: string; sub: string; image: string; onOpen: () => void }) {
  return <CardSurface asChild lift padding="none" className="co-access">
    <button type="button" onClick={onOpen}>
      <img className="co-access-img" src={image} alt="" loading="lazy" />
      <span className="co-access-body"><b className="co-access-title">{title}</b><span className="co-strong">{line}</span><span className="co-small">{sub}</span></span>
    </button>
  </CardSurface>;
}

const ALL_TYPES = AGENDA_SOURCES.map(s => s.id);

function CalendarHome({ props, go, detailActions }: { props: PageProps; go: (loc: CoachingLocation) => void; detailActions: EventDetailActions }) {
  const { snap } = useCoaching();
  const [month, setMonth] = useState(() => { const d = parseIso(DEMO_TODAY); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [types, setTypes] = useState<string[]>(ALL_TYPES);
  const items = useMemo(() => calendarItems(snap.appointments).filter(i => types.includes(i.type)), [snap.appointments, types]);
  const inMonth = items.filter(i => { const d = parseIso(i.date); return d.getMonth() === month.getMonth() && d.getFullYear() === month.getFullYear(); });
  const shift = (n: number) => setMonth(m => new Date(m.getFullYear(), m.getMonth() + n, 1));
  const open = (i: CalItem) => i.appointment
    ? props.openPeek({ title: i.title, content: <AppointmentPanel id={i.appointment} onShop={() => props.navigate("shop")} /> })
    : props.openPeek({ title: i.title, content: <EventPanel item={i} actions={detailActions} /> });
  const nextAppt = snap.appointments.filter(isUpcoming).sort((x, y) => (x.date + x.time).localeCompare(y.date + y.time))[0];
  const credits = SERVICES.reduce((n, s) => n + snap.balances[s.coin], 0);

  return <div className="co-home">
    <div className="co-cal">
      <div className="co-toolbar">
        <div className="co-nav">
          <Button variant="ghost" shape="icon" size="sm" aria-label="Mois précédent" onClick={() => shift(-1)}><ChevronLeft size={18} aria-hidden="true" /></Button>
          <h2 className="co-month-label" aria-live="polite">{monthLabel(month)}</h2>
          <Button variant="ghost" shape="icon" size="sm" aria-label="Mois suivant" onClick={() => shift(1)}><ChevronRight size={18} aria-hidden="true" /></Button>
        </div>
      <Popover>
        <PopoverTrigger asChild><Button variant="ghost" size="sm"><SlidersHorizontal size={16} aria-hidden="true" />Filtres</Button></PopoverTrigger>
        <PopoverContent align="start" className="co-filter-panel">
          <p className="co-filter-title">Types d’événements</p>
          <ChipGroup className="co-filters" label="Types d’événements" multiple value={types} onValueChange={setTypes} options={AGENDA_SOURCES.map(s => ({ value: s.id, label: s.name }))} />
        </PopoverContent>
      </Popover>

      </div>
      <ListView items={inMonth} onOpen={open} />
    </div>

    <aside className="co-aside" aria-label="Accès Coaching">
      <AccessCard title="Consultations" image={eventVisual("consult")} onOpen={() => go({ view: "consult" })}
        line={nextAppt ? `Prochain : ${capitalize(longDate(nextAppt.date))} · ${hourLabel(nextAppt.time)}` : "Aucun rendez-vous à venir"}
        sub={`${credits} pièce${credits > 1 ? "s" : ""} disponible${credits > 1 ? "s" : ""} · réserver, reporter, annuler`} />
      <AccessCard title="Team Genius" image={eventVisual("tg")} onOpen={() => go({ view: "genius" })}
        line={`Prochaine séance : ${capitalize(longDate(GENIUS_NEXT.date))} · ${hourLabel(GENIUS_NEXT.start)}`}
        sub={`${GENIUS_PAST.length} séances passées · replays, ressources, pistes`} />
    </aside>
  </div>;
}

// ── Page Consultations ───────────────────────────────────────────────────────

function BackBar({ label, onBack }: { label: string; onBack: () => void }) {
  return <div className="co-back"><Button variant="ghost" size="sm" leading={<ArrowLeft size={16} aria-hidden="true" />} onClick={onBack}>{label}</Button></div>;
}

function useTitleFocus(key: string) {
  const ref = useRef<HTMLHeadingElement>(null);
  useEffect(() => { document.getElementById("tc-main")?.scrollTo({ top: 0 }); ref.current?.focus({ preventScroll: true }); }, [key]);
  return ref;
}

function ConsultPage({ props, back, book }: { props: PageProps; back: () => void; book: (service: ServiceId) => void }) {
  const { snap } = useCoaching();
  const title = useTitleFocus("consult");
  const upcoming = snap.appointments.filter(isUpcoming).sort((x, y) => (x.date + x.time).localeCompare(y.date + y.time));
  const past = snap.appointments.filter(a => !isUpcoming(a)).sort((x, y) => y.date.localeCompare(x.date));
  const next = upcoming[0];
  const openAppt = (a: Appointment) => props.openPeek({ title: serviceById(a.service).name, content: <AppointmentPanel id={a.id} onShop={() => props.navigate("shop")} /> });
  const row = (a: Appointment) => { const sv = serviceById(a.service); return <li key={a.id}>
    <button type="button" className="co-item co-type-consult" onClick={() => openAppt(a)}>
      <Coin kind={sv.coin} />
      <span className="co-item-text"><b>{sv.name}</b><span>{whenLabel({ date: a.date, start: a.time, end: addMinutes(a.time, sv.duration) })}</span></span>
      <Badge size="sm" tone={isUpcoming(a) ? "success" : "neutral"}>{statusLabel(a)}</Badge>
    </button>
  </li>; };

  return <div className="co-page">


    {next ? <CardSurface asChild lift padding="none" className="co-next"><button type="button" onClick={() => openAppt(next)}>
      <img className="co-next-img" src={eventVisual("consult")} alt="" />
      <span className="co-next-body"><span className="co-kicker">Prochain rendez-vous</span><b className="co-next-title">{serviceById(next.service).name}</b>
        <span className="co-strong">{whenLabel({ date: next.date, start: next.time, end: addMinutes(next.time, serviceById(next.service).duration) })}</span>
        <span className="co-small">{serviceById(next.service).mode} · {paymentLabel(next)}</span></span>
    </button></CardSurface> : <EmptyState title="Aucun rendez-vous à venir" description="Choisis une prestation ci-dessous pour réserver." titleAs="h3" />}

    <section className="co-stack">
      <div className="co-section-head"><h3 className="co-h3">Réserver</h3><span className="co-small">Solde par type de pièce</span></div>
      <div className="co-services">{SERVICES.map(sv => { const b = snap.balances[sv.coin]; return <CardSurface key={sv.id} padding="md" className="co-service">
        <div className="co-row"><Coin kind={sv.coin} size="lg" /><div className="co-item-text"><b>{sv.name}</b><span>{sv.mode} · {sv.duration} min</span></div></div>
        <p className="co-small"><b>{b}</b> {b > 1 ? sv.coinPlural : sv.coinName} · sinon {sv.price} € depuis la Boutique</p>
        <Button size="sm" variant={b > 0 ? "primary" : "secondary"} onClick={() => book(sv.id)}>{b > 0 ? "Réserver" : "Voir les options"}</Button>
      </CardSurface>; })}</div>
      <p className="co-small">{CONSULT_POLICY}</p>
    </section>

    {upcoming.length > 1 || past.length ? <section className="co-stack">
      <h3 className="co-h3">Mes rendez-vous</h3>
      {upcoming.length > 1 ? <><p className="co-kicker">Ensuite</p><ul className="co-plain co-stack-sm">{upcoming.slice(1).map(row)}</ul></> : null}
      {past.length ? <><p className="co-kicker">Passés et annulés</p><ul className="co-plain co-stack-sm">{past.map(row)}</ul></> : null}
    </section> : null}
  </div>;
}

// ── Team Genius ──────────────────────────────────────────────────────────────

const sessionStatus = (s: GeniusSession, catchups: Record<string, string>): { label: string; tone: BadgeTone } =>
  s.missed ? (catchups[s.id] ? { label: "Replay ouvert", tone: "success" } : { label: "Manquée · rattrapage possible", tone: "warning" }) : { label: "Participation reconnue", tone: "success" };

function GeniusPage({ back, go }: { back: () => void; go: (loc: CoachingLocation) => void }) {
  const { snap, actions } = useCoaching();
  const title = useTitleFocus("genius");
  const [question, setQuestion] = useState("");
  const send = () => { const text = question.trim(); if (!text) return; actions.askQuestion(text); setQuestion(""); };
  return <div className="co-page">


    <CardSurface padding="none" className="co-poster">
      <img className="co-poster-img" src={eventVisual("tg")} alt="" />
      <div className="co-poster-body">
        <span className="co-kicker">Prochaine séance · {GENIUS_NEXT.theme}</span>
        <h3 className="co-poster-title">{GENIUS_NEXT.title}</h3>
        <p className="co-strong">{capitalize(longDate(GENIUS_NEXT.date))} · {hourLabel(GENIUS_NEXT.start)}–{hourLabel(GENIUS_NEXT.end)}</p>
        <p>{GENIUS_NEXT.desc}</p>
        <div className="co-actions co-actions-start"><Button disabled>Rejoindre la visio</Button></div>
        <p className="co-small">Le bouton s’active de {hourLabel(GENIUS_NEXT.window[0])} à {hourLabel(GENIUS_NEXT.window[1])} le jour de la séance. L’appui dans cette fenêtre vaut participation reconnue.</p>
      </div>
    </CardSurface>

    <section className="co-stack">
      <div className="co-section-head"><h3 className="co-h3">Questions pour la séance</h3><span className="co-small">{snap.questions.length} questions</span></div>
      <ul className="co-plain co-stack-sm">{snap.questions.map(q => <li key={q.id} className="co-question">
        <Avatar size="sm" name={q.author} /><div className="co-item-text"><b>{q.author} <span className="co-muted">· {q.time}</span></b><span>{q.text}</span></div>
      </li>)}</ul>
      <Composer value={question} onChange={setQuestion} onSend={send} placeholder="Ta question pour la séance…" />
    </section>

    <section className="co-stack">
      <h3 className="co-h3">Séances passées</h3>
      <div className="co-sessions">{GENIUS_PAST.map(s => { const st = sessionStatus(s, snap.catchups); return <CardSurface key={s.id} asChild lift padding="none" className="co-session">
        <button type="button" onClick={() => go({ view: "genius", session: s.id })}>
          <img className="co-session-img" src={eventVisual("tg")} alt="" loading="lazy" />
          <span className="co-session-body">
            <span className="co-row"><Badge size="sm" tone="accent">{s.theme}</Badge><Badge size="sm" tone={st.tone}>{st.label}</Badge></span>
            <b className="co-session-title">{s.title}</b>
            <span className="co-small">{capitalize(longDate(s.date))}{s.duration ? ` · ${s.duration}` : ""}</span>
          </span>
        </button>
      </CardSurface>; })}</div>
    </section>
  </div>;
}

/** Séance passée, page dédiée (même architecture que la page Ressource d'Apprendre) : replay, ressources, pistes. */
function GeniusSessionPage({ session, back }: { session: GeniusSession; back: () => void }) {
  const { snap, actions } = useCoaching();
  const title = useTitleFocus(session.id);
  const [reason, setReason] = useState("");
  const unlocked = !session.missed || !!snap.catchups[session.id];
  const st = sessionStatus(session, snap.catchups);
  return <div className="co-page co-reader">
    <article className="co-stack-lg">
      <header className="co-head"><p className="co-kicker">Team Genius · {session.theme}</p><h2 className="co-title" ref={title} tabIndex={-1}>{session.title}</h2>
        <div className="co-row"><Badge size="sm">{capitalize(longDate(session.date))}</Badge>{session.duration ? <Badge size="sm">{session.duration}</Badge> : null}<Badge size="sm" tone={st.tone}>{st.label}</Badge></div>
      </header>
      <p className="co-lead">{session.desc}</p>

      {unlocked ? <div className="co-replay">
        <img className="co-replay-img" src={eventVisual("tg")} alt="" />
        <div className="co-replay-note"><PlayCircle size={20} aria-hidden="true" /><span>Replay indisponible</span></div>
      </div> : <CardSurface variant="sunken" padding="md" className="co-stack-sm">
        <p className="co-strong">Rattrapage</p>
        <p className="co-small">{GENIUS_CATCHUP_POLICY}</p>
        {session.homework ? <p className="co-small">Devoir de la séance : {session.homework}</p> : null}
        <Textarea label="Motif" rows={2} value={reason} onChange={e => setReason(e.target.value)} placeholder="Une phrase suffit" />
        <div className="co-actions co-actions-start"><Button disabled={!reason.trim()} onClick={() => actions.requestCatchup(session.id, reason.trim())}>Envoyer un justificatif</Button></div>
      </CardSurface>}
      {session.missed && snap.catchups[session.id] ? <Alert tone="success" title="Replay de cette séance ouvert">Justificatif enregistré pour cette séance uniquement.</Alert> : null}

      {session.resources.length ? <section className="co-stack-sm">
        <h3 className="co-h3">Ressources</h3>
        <ul className="co-plain co-stack-sm">{session.resources.map(r => <li key={r.id} className="co-file">
          <FileText size={18} aria-hidden="true" /><span className="co-item-text"><b>{r.title}</b><span>{r.format}</span></span>
          <Button size="sm" variant="secondary" disabled>Télécharger</Button>
        </li>)}</ul>
      </section> : null}

      {session.leads.length ? <section className="co-stack-sm">
        <h3 className="co-h3">Pistes d’action</h3>
        <ul className="co-plain co-stack-sm">{session.leads.map(l => { const added = !!snap.leads[l.id]; return <li key={l.id} className="co-lead-row">
          <span>{l.title}</span>
          <Button size="sm" variant={added ? "ghost" : "secondary"} aria-pressed={added} onClick={() => actions.toggleLead(l.id)}>{added ? "Ajoutée à mes actions" : "Ajouter à mes actions"}</Button>
        </li>; })}</ul>
      </section> : null}
    </article>
  </div>;
}

// ── Entrée ───────────────────────────────────────────────────────────────────

export function CoachingExperience(props: PageProps) {
  const [loc, go] = useCoachingLocation();
  const home = useCallback(() => go({}), [go]);
  const book = useCallback((service: ServiceId) => props.openPeek({ title: `Réserver · ${serviceById(service).name}`,
    content: <BookingPanel serviceId={service} onShop={() => props.navigate("shop")} /> }), [props]);
  // Depuis le sidepeek : navigate() ferme le détail (même route), puis la vue contextuelle prend le hash.
  const detailActions: EventDetailActions = useMemo(() => ({
    onBook: book,
    onRoute: props.navigate,
    onView: next => { props.navigate("accompagnement"); go(next); },
  }), [book, props, go]);

  const session = loc.session ? GENIUS_PAST.find(s => s.id === loc.session) : undefined;
  return <div className="co-root">
    {loc.view === "consult" ? <ConsultPage props={props} back={home} book={book} />
      : loc.view === "genius" && session ? <GeniusSessionPage session={session} back={() => go({ view: "genius" })} />
      : loc.view === "genius" ? <GeniusPage back={home} go={go} />
      : <CalendarHome props={props} go={go} detailActions={detailActions} />}
  </div>;
}

export default CoachingExperience;

/** Couleurs par type, exposées pour un éventuel consommateur (Accueil) : mêmes classes, mêmes jetons. */
export const coachingTypeClass = (type: AgendaSourceId) => `co-type-${type}`;
